import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, authHeaders, authHeadersFormData } from './apiHelpers';
import { SiteInfo } from './useSiteInfo';
import { SiteImage } from './useSiteImages';

export interface SiteDetail {
    column: string;
    value: string;
    isPicture: boolean;
}

export interface AdminSiteDetail {
    column: string;
    value: string;
    defaultValue: string;
    isPicture: boolean;
}

export interface SiteInfoDetails {
    siteDetail: SiteDetail[];
    siteInfo: SiteInfo;
    siteImages: SiteImage[];
    countryCode: string;
}

export interface SiteAdminInfoDetails {
    siteInfo: SiteInfo;
    siteDetail: AdminSiteDetail[];
}

export function useSiteInfoDetails(siteId: number, countryCode: string) {
    return useQuery<SiteInfoDetails, Error>({
        queryKey: ['siteDetails', siteId, countryCode],
        queryFn: () => apiFetch<SiteInfoDetails>(`api/SiteInfo/GetSiteDetails?siteId=${siteId}&countryCode=${encodeURIComponent(countryCode)}`, {
            method: 'GET',
            headers: authHeaders()
        }),
        enabled: siteId > 0
    });
}

export function useAdminSiteInfoDetails(siteId: number, activeCountryCode: string, defaultCountryCode?: string) {
    return useQuery<SiteAdminInfoDetails, Error>({
        queryKey: ['adminSiteDetails', siteId, activeCountryCode, defaultCountryCode ?? null],
        queryFn: () => apiFetch<SiteAdminInfoDetails>(
            `api/SiteInfo/GetAdminSiteDetails?siteId=${siteId}&activeCountryCode=${encodeURIComponent(activeCountryCode)}` +
            (defaultCountryCode ? `&defaultCountryCode=${encodeURIComponent(defaultCountryCode)}` : ''),
            { method: 'GET', headers: authHeaders() }
        ),
        enabled: siteId > 0
    });
}

export function useSiteDetail(siteId: string, countryCode: string) {
    return useQuery<SiteDetail[], Error>({
        queryKey: ['siteDetail', siteId, countryCode],
        queryFn: () => apiFetch<SiteDetail[]>(`api/SiteInfo/GetSiteDetail?siteId=${siteId}&countryCode=${encodeURIComponent(countryCode)}`, {
            method: 'GET',
            headers: authHeaders()
        }),
        enabled: !!siteId && !!countryCode
    });
}

export function useUpsertSiteDetail() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { uniqueSiteId: number; data: FormData }>({
        mutationFn: ({ uniqueSiteId, data }) => apiFetch<void>(`api/SiteInfo/UpsertSiteDetail?siteId=${uniqueSiteId}`, {
            method: 'POST',
            body: data,
            headers: authHeadersFormData()
        }),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['siteDetail'] });
            queryClient.invalidateQueries({ queryKey: ['adminSiteDetails', variables.uniqueSiteId] });
        }
    });
}
